import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import type { UseMutationOptions, UseMutationResult } from 'react-query';
import { useGetSupplierProfilesApi } from './use-get-supplier-profiles-api';

const endpoint = 'ums/v1/cms/supplier-profiles';
export const createSupplierProfileEndpointIdentifier = endpoint;

export interface CreateSupplierProfileBody {
  // Exactly one of supplierId (existing supplier) or supplier (new supplier) must be set.
  supplierId?: number;
  supplier?: {
    name: string;
    countryCode: string;
    cutOffTime?: string;
    minimumOrder?: number;
    email?: string;
    isActive?: boolean;
    isPublic?: boolean;
    imageUrl?: string;
  };
  profile?: {
    businessName?: string;
    category?: string;
    moq?: string;
    delivery?: string;
    paymentTerms?: string;
    commissionRate?: number;
    billingLegalName?: string;
    billingAddress?: string;
    taxRegistrationNo?: string;
  };
}

type TData = Record<string, any>;
type TError = AxiosError;

type TVariables = {
  body: CreateSupplierProfileBody;
};

type Options = Omit<UseMutationOptions<TData, TError, TVariables, unknown>, 'mutationFn'>;
type ReturnType = UseMutationResult<TData, TError, TVariables>;

export function useCreateSupplierProfileApi(options?: Options): ReturnType {
  const { refetch: refetchSupplierProfiles } = useGetSupplierProfilesApi({ enabled: false });
  const toast = useCustomToast();

  const mutationFn = async ({ body }: TVariables) => {
    try {
      const { data } = await http.post(endpoint, body);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper(mutationFn, {
    mutationKey: [endpoint],
    onSuccess: () => {
      refetchSupplierProfiles();

      toast({
        description: 'New supplier has been created successfully',
        status: 'success',
      });
    },
    ...options,
  });
}
